import { useMemo } from "react";

import { sortByStandaloneGifts } from "@jti/commerce/shared";
import type { OrderItem } from "@jti/magento";

/**
 * @description groups cart items into
 * - payable items
 * - gift items (grouped by SKU)
 *
 * @example
 * passed argument:
 * [
 *       product_SKU_1,
 *       product_SKU_2,
 *       gift_SKU_3,
 *       gift_SKU_3,
 *       gift_SKU_4,
 * ]
 *
 * returned value:
 *
 * payableItems: [
 *       product_SKU_1,
 *       product_SKU_2,
 * ]
 * groupedGiftItems: [
 *       gift_SKU_3, // updated quantity
 *       gift_SKU_4,
 * ]
 */
export const useCartItemsGrouping = (items: OrderItem[]) =>
  useMemo(() => {
    const payableItems: OrderItem[] = [];
    const giftItemsMap = new Map<string, OrderItem>();

    items.forEach((item) => {
      if (!item.isGift) {
        payableItems.push(item);
      } else {
        const groupedItem = giftItemsMap.get(item.sku);

        if (!groupedItem) {
          giftItemsMap.set(item.sku, item);
        } else {
          const updatedQuantity = (groupedItem.quantity || 1) + (item.quantity || 1);
          giftItemsMap.set(item.sku, { ...groupedItem, quantity: updatedQuantity });
        }
      }
    });

    return { payableItems, groupedGiftItems: Array.from(giftItemsMap.values()).sort(sortByStandaloneGifts) };
  }, [items]);
